/**
 * Hardcoded list of all levels available when choosing a level for a given
 * course
 */
export const levels = [
  "Vuggestue",
  "Børnehave",
  "0. klasse",
  "1. klasse",
  "2. klasse",
  "3. klasse",
  "4. klasse",
  "5. klasse",
  "6. klasse",
  "7. klasse",
  "8. klasse",
  "9. klasse",
  "10. klasse",
];

export type GradeLevel =
  | {
      id: "Dagtilbud";
      institution: "Dagtilbud";
      sortValue: number;
    }
  | {
      id: "Børnehaveklassen";
      institution: "Børnehaveklassen";
      sortValue: number;
    }
  | {
      id: `Grundskole_${number}`;
      institution: "Grundskole";
      level: number;
      sortValue: number;
    }
  | {
      id: `HF_${number}`;
      institution: "HF";
      level: number;
      sortValue: number;
    }
  | {
      id: `Gymnasium_${string}`;
      institution: "Gymnasium";
      level: string;
      sortValue: number;
    }
  | {
      id: "Andet";
      institution: "Andet";
      level: string;
      sortValue: number;
    };

/**
 * parses a plain text, human-readable, grade level label and splits it into
 * institution and grade for easier processing during render-time in the
 * frontend
 */
export function transformGradeLevel(input: string): GradeLevel {
  switch (input.toLowerCase()) {
    case "dagtilbud":
      return {
        id: "Dagtilbud",
        institution: "Dagtilbud",
        sortValue: 1000,
      };

    case "børnehaveklassen":
      return {
        id: "Børnehaveklassen",
        institution: "Børnehaveklassen",
        sortValue: 1100,
      };

    case "0. klasse":
    case "1. klasse":
    case "2. klasse":
    case "3. klasse":
    case "4. klasse":
    case "5. klasse":
    case "6. klasse":
    case "7. klasse":
    case "8. klasse":
    case "9. klasse":
    case "10. klasse": {
      const level = parseInt(input, 10);
      return {
        id: `Grundskole_${level}`,
        institution: "Grundskole",
        level,
        sortValue: 2000 + level,
      };
    }

    case "1 hf":
    case "2 hf": {
      const level = parseInt(input, 10);
      return {
        id: `HF_${level}`,
        institution: "HF",
        level,
        sortValue: 3000 + level,
      };
    }

    case "a-niveau":
    case "b-niveau":
    case "c-niveau": {
      const level = input.charAt(0).toUpperCase();
      return {
        id: `Gymnasium_${level}`,
        institution: "Gymnasium",
        level,
        sortValue: 4000 + 500 - level.charCodeAt(0),
      };
    }

    default:
      return {
        id: "Andet",
        institution: "Andet",
        level: input,
        sortValue: 5000,
      };
  }
}

export function stringifyGradeLevelRange(gradeLevels: GradeLevel[]): string {
  const output: string[] = [];
  const segments = gradeLevels.reduce(
    (acc, gradeLevel) => {
      switch (gradeLevel.institution) {
        case "Dagtilbud":
        case "Børnehaveklassen":
          acc.Preschool.push(gradeLevel.institution);
          break;

        case "Grundskole":
          acc.Grundskole.push(gradeLevel.level);
          break;

        case "HF":
          acc.HF.push(gradeLevel.level);
          break;

        case "Gymnasium":
          acc.Gymnasium.push(gradeLevel.level);
          break;

        default:
          acc.Andet.push(gradeLevel.level);
          break;
      }

      return acc;
    },
    {
      Preschool: [] as string[],
      Grundskole: [] as number[],
      HF: [] as number[],
      Gymnasium: [] as string[],
      Andet: [] as string[],
    },
  );

  output.push(...segments.Preschool);

  if (segments.Grundskole.length === 1) {
    output.push(`${segments.Grundskole[0]}. klasse`);
  } else if (segments.Grundskole.length > 1) {
    output.push(
      `${segments.Grundskole[0]}.-${
        segments.Grundskole[segments.Grundskole.length - 1]
      }. klasse`,
    );
  }

  if (segments.HF.length === 1) {
    output.push(`${segments.HF[0]}. HF`);
  } else if (segments.HF.length > 1) {
    output.push(
      `${segments.HF[0]}.-${segments.HF[segments.HF.length - 1]}. HF`,
    );
  }

  if (segments.Gymnasium.length === 1) {
    output.push(`${segments.Gymnasium[0]}-niveau`);
  } else if (segments.Gymnasium.length > 1) {
    output.push(
      `${segments.Gymnasium[0]}-${
        segments.Gymnasium[segments.Gymnasium.length - 1]
      }-niveau`,
    );
  }

  output.push(...segments.Andet);

  return output.join(", ");
}
